// =============================================================================
// BRANDING ASSISTANT — Free-form AI chatbot for branding suggestions
//
// Floating chat widget (FAB + expandable panel). Users can ask anything
// about branding and get AI-powered suggestions with one-click "Apply" buttons.
// =============================================================================

import { useState, useRef, useEffect, useCallback } from 'react';
import { Sparkles, X, Send, Loader2 } from 'lucide-react';

// ── Constants ────────────────────────────────────────────────────────────────

const PLATFORM_URL = import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const ASSISTANT_API = `${PLATFORM_URL}/functions/v1/branding-assistant`;

interface Action {
  field: string;
  value: string;
  label: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  actions?: Action[];
}

interface BrandingAssistantProps {
  form: Record<string, any>;
  set: (field: string, value: any) => void;
  onFieldApplied?: (field: string) => void;
}

const FIELD_LABELS: Record<string, string> = {
  primary_color: 'Primary Color',
  secondary_color: 'Secondary Color',
  hero_headline: 'Headline',
  hero_subtitle: 'Subtitle',
  cta_label: 'CTA Button',
  company_name: 'Company Name',
  app_name: 'App Name',
  support_email: 'Support Email',
};

const QUICK_PROMPTS = [
  'Suggest colors for my brand',
  'Give me headline ideas',
  'Suggest a CTA button text',
  'Help me write a subtitle',
];

// ── Component ────────────────────────────────────────────────────────────────

export default function BrandingAssistant({ form, set, onFieldApplied }: BrandingAssistantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [appliedActions, setAppliedActions] = useState<Set<string>>(new Set());

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hi! I'm your branding assistant. Ask me anything — I can suggest colors, headlines, subtitles, button text, and more. Try one of the quick prompts below or type your own question!",
      }]);
    }
  }, [isOpen]);

  // ── Send message ──────────────────────────────────────────────────────────

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || isLoading) return;

    const userMsg: Message = { role: 'user', content: text.trim() };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInputValue('');
    setIsLoading(true);

    try {
      // Build chat history for API (only role + content)
      const chatHistory = updatedMessages.map(m => ({
        role: m.role,
        content: m.content,
      }));

      // Build branding context from current form values
      const brandingContext: Record<string, string> = {};
      if (form.app_name) brandingContext.app_name = form.app_name;
      if (form.primary_color) brandingContext.primary_color = form.primary_color;
      if (form.secondary_color) brandingContext.secondary_color = form.secondary_color;
      if (form.hero_headline) brandingContext.hero_headline = form.hero_headline;
      if (form.hero_subtitle) brandingContext.hero_subtitle = form.hero_subtitle;
      if (form.cta_label) brandingContext.cta_label = form.cta_label;
      if (form.company_name) brandingContext.company_name = form.company_name;

      const res = await fetch(ASSISTANT_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          messages: chatHistory,
          branding_context: brandingContext,
        }),
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        console.error('Branding assistant API error:', res.status, errText);
        throw new Error(`API error (${res.status})`);
      }

      const data = await res.json();
      const botMsg: Message = {
        role: 'assistant',
        content: data.reply || "Sorry, I couldn't generate a response.",
        actions: data.actions || undefined,
      };
      setMessages(prev => [...prev, botMsg]);

    } catch (err) {
      console.error('Branding assistant error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, I couldn't reach the AI service right now. Please try again in a moment.",
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading, form]);

  // ── Apply action ──────────────────────────────────────────────────────────

  const handleApply = useCallback((action: Action) => {
    set(action.field, action.value);
    setAppliedActions(prev => new Set(prev).add(`${action.field}:${action.value}`));
    // Navigate to the field and highlight it
    onFieldApplied?.(action.field);
  }, [set, onFieldApplied]);

  // ── Handle send ───────────────────────────────────────────────────────────

  const handleSend = useCallback(() => {
    sendMessage(inputValue);
  }, [inputValue, sendMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // ── FAB (closed state) ────────────────────────────────────────────────────

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-[72px] h-[72px] rounded-full bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center group"
        title="Branding Assistant"
      >
        {/* Rotating "Ask me!" text around the circle */}
        <svg className="absolute inset-0 w-full h-full animate-[spin_12s_linear_infinite]" viewBox="0 0 72 72">
          <defs>
            <path id="circle-text-path" d="M 36,36 m -25,0 a 25,25 0 1,1 50,0 a 25,25 0 1,1 -50,0" />
          </defs>
          <text fill="white" fontSize="10" fontWeight="600" letterSpacing="3">
            <textPath href="#circle-text-path">Ask me! ✦ Ask me! ✦ </textPath>
          </text>
        </svg>
        <Sparkles className="w-5 h-5 relative z-10 group-hover:animate-pulse" />
      </button>
    );
  }

  // ── Chat Panel (open state) ───────────────────────────────────────────────

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-2rem)] h-[520px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          <span className="font-semibold text-sm">Branding Assistant</span>
        </div>
        <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ scrollbarWidth: 'thin' }}>
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] ${msg.role === 'user'
              ? 'bg-teal-600 text-white rounded-2xl rounded-br-md px-3.5 py-2.5'
              : 'bg-slate-100 text-slate-800 rounded-2xl rounded-bl-md px-3.5 py-2.5'
            }`}>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>

              {/* Action buttons */}
              {msg.actions && msg.actions.length > 0 && (
                <div className="mt-2.5 flex flex-col gap-1.5">
                  {msg.actions.map((action, j) => {
                    const isApplied = appliedActions.has(`${action.field}:${action.value}`);
                    return (
                      <button
                        key={j}
                        onClick={() => handleApply(action)}
                        disabled={isApplied}
                        className={`flex items-center gap-2 text-left text-xs px-3 py-2 rounded-lg border transition-all ${
                          isApplied
                            ? 'bg-green-50 border-green-200 text-green-700 cursor-default'
                            : 'bg-white border-slate-200 text-slate-700 hover:border-teal-300 hover:bg-teal-50 cursor-pointer'
                        }`}
                      >
                        {/* Color swatch for color fields */}
                        {action.field.includes('color') && (
                          <div
                            className="w-5 h-5 rounded-md border border-slate-300 flex-shrink-0"
                            style={{ backgroundColor: action.value }}
                          />
                        )}
                        <span className="flex-1">
                          {isApplied ? '✓ Applied' : action.label}
                          <span className="ml-1 text-[10px] opacity-60">
                            ({FIELD_LABELS[action.field] || action.field})
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2 text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-xs">Thinking...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick prompts — show only when no user messages yet */}
      {messages.length <= 1 && !isLoading && (
        <div className="px-4 pb-2 flex flex-wrap gap-1.5 shrink-0">
          {QUICK_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              onClick={() => sendMessage(prompt)}
              className="text-xs px-3 py-1.5 rounded-full border border-teal-200 text-teal-600 hover:bg-teal-50 transition-colors"
            >
              {prompt}
            </button>
          ))}
        </div>
      )}

      {/* Input bar */}
      <div className="px-3 py-3 border-t border-slate-100 shrink-0">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about colors, headlines, branding..."
            className="flex-1 text-sm px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:border-teal-300 focus:ring-1 focus:ring-teal-200 bg-white text-slate-800 placeholder:text-slate-400"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            className="w-9 h-9 rounded-xl bg-teal-600 text-white flex items-center justify-center hover:bg-teal-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
